import { useRef, useState } from 'react';
import type { OpportunityAttachment } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { formatDateTime } from '../../utils/date.js';
import {
  downloadOpportunityAttachment,
  fetchAttachmentPreviewUrl,
  useDeleteOpportunityAttachment,
  useOpportunityAttachments,
  useUploadOpportunityAttachment,
} from './api.js';

interface OpportunityAttachmentsSectionProps {
  opportunityId: string;
  canEdit: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export function OpportunityAttachmentsSection({
  opportunityId,
  canEdit,
}: OpportunityAttachmentsSectionProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ name: string; url: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const attachmentsQuery = useOpportunityAttachments(opportunityId);
  const uploadMutation = useUploadOpportunityAttachment();
  const deleteMutation = useDeleteOpportunityAttachment();

  const attachments = attachmentsQuery.data ?? [];

  const messageOf = (error: unknown, fallback: string): string =>
    error instanceof ApiError ? error.message : fallback;

  // Uploads files one at a time (the API takes a single file per request), so a
  // multi-file drop or picker selection enqueues each file in turn.
  const uploadFiles = (files: FileList | File[]): void => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setActionError(null);
    for (const file of list) {
      uploadMutation.mutate(
        { opportunityId, file },
        { onError: (error) => setActionError(messageOf(error, 'Upload failed.')) },
      );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    e.target.value = '';
    if (files) uploadFiles(files);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
    if (!canEdit) return;
    if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    if (!canEdit) return;
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    // Only clear when the pointer actually leaves the dropzone (not a child element).
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragOver(false);
  };

  const handlePreview = async (attachment: OpportunityAttachment): Promise<void> => {
    setActionError(null);
    setPreviewLoading(true);
    try {
      const { url } = await fetchAttachmentPreviewUrl(opportunityId, attachment.id);
      setPreview({ name: attachment.fileName, url });
    } catch (error) {
      setActionError(messageOf(error, 'Could not open a preview.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (attachment: OpportunityAttachment): Promise<void> => {
    setActionError(null);
    try {
      await downloadOpportunityAttachment(opportunityId, attachment.id);
    } catch (error) {
      setActionError(messageOf(error, 'Download failed.'));
    }
  };

  const handleDelete = (attachmentId: string): void => {
    setActionError(null);
    deleteMutation.mutate(
      { opportunityId, attachmentId },
      {
        onSuccess: () => setConfirmId(null),
        onError: (error) => setActionError(messageOf(error, 'Delete failed.')),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {canEdit ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-center transition ${
            dragOver
              ? 'border-brand-500 bg-brand-50'
              : 'border-slate-300 bg-slate-50'
          }`}
        >
          <p className="text-sm text-slate-600">
            Drag &amp; drop files here, or
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            isLoading={uploadMutation.isPending}
          >
            Choose files
          </Button>
          <p className="text-xs text-slate-400">
            Files are stored in the PSA SharePoint library and can be previewed without
            downloading.
          </p>
        </div>
      ) : null}

      {actionError ? (
        <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{actionError}</div>
      ) : null}

      {attachmentsQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading files…</p>
      ) : attachments.length === 0 ? (
        <p className="text-sm text-slate-500">No files attached yet.</p>
      ) : (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">
                  {attachment.fileName}
                </p>
                <p className="truncate text-xs text-slate-500">
                  {formatBytes(attachment.size)} · {attachment.uploadedByName} ·{' '}
                  {formatDateTime(attachment.createdAt)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  onClick={() => void handlePreview(attachment)}
                  disabled={previewLoading}
                >
                  Preview
                </Button>
                <Button
                  variant="ghost"
                  className="px-2 py-1 text-xs"
                  onClick={() => void handleDownload(attachment)}
                >
                  Download
                </Button>
                {canEdit ? (
                  confirmId === attachment.id ? (
                    <>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                        onClick={() => handleDelete(attachment.id)}
                        isLoading={deleteMutation.isPending}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        onClick={() => setConfirmId(null)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                      onClick={() => setConfirmId(attachment.id)}
                    >
                      Delete
                    </Button>
                  )
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.name ?? 'Preview'}
        size="wide"
        fillHeight
        footer={
          <Button variant="secondary" onClick={() => setPreview(null)}>
            Close
          </Button>
        }
      >
        {preview ? (
          <iframe
            title={preview.name}
            src={preview.url}
            className="h-[70vh] w-full rounded-lg border border-slate-200"
          />
        ) : null}
      </Modal>
    </div>
  );
}
